import * as debug from '../../utils/debug';
import { utils } from '../../utils/utils';
import { Locale } from '../locale/locale';

// Settings and Options
const COMPONENT_NAME = 'notificationbadge';

// Options for the position of dot
const NOTIFICATION_BADGE_POSITION_OPTIONS = ['upper-left', 'upper-right', 'lower-left', 'lower-right'];

// Options for the color of dot
const NOTIFICATION_BADGE_COLOR_OPTIONS = ['neutral', 'alert', 'warning', 'yield', 'complete', 'progress', 'caution'];

const NOTIFICATION_BADGE_DEFAULTS = {
  position: NOTIFICATION_BADGE_POSITION_OPTIONS[1],
  color: NOTIFICATION_BADGE_COLOR_OPTIONS[0],
  label: undefined,
  visible: true,
  attribute: null,
  audibleLabel: null,
};

/**
 * Notification Badge - show and indicates that something has change.
 * @class NotificationBadge
 * @param {string} element The plugin element for the constuctor
 * @param {string} [settings] The settings element.
 * @param {string} [settings.position] The placement of notification badge.
 * @param {string} [settings.color] The color of the notification badge.
 * @param {string} [settings.label] The label of the notification badge.
 * @param {boolean} [settings.visible=true] Whether the notification badge is visible.
 * @param {array|object} [settings.attributes=null] Add extra attributes like id's to the element e.g. `attributes: { name: 'id', value: 'my-unique-id' }`
 */

function NotificationBadge(element, settings) {
  this.settings = utils.mergeSettings(element, settings, NOTIFICATION_BADGE_DEFAULTS);
  this.element = $(element);
  debug.logTimeStart(COMPONENT_NAME);
  this.init();
  debug.logTimeStart(COMPONENT_NAME);
}

// Plugin Methods
NotificationBadge.prototype = {

  /**
   * Do initialization, build up and / or add events.
   * @returns {object} The Component prototype, useful for chaining.
   */
  init() {
    return this.build().handleEvents();
  },

  /**
   * Add any needed markup to the component.
   * @returns {object} The Component prototype, useful for chaining.
   * @private
   */
  build() {
    // Early return for the case where the element doesn't need additional markup
    const isTertiaryButton = this.element.hasClass('btn-tertiary');
    const isIconButton = this.element.hasClass('btn-icon');
    const isMenuTrigger = this.element.hasClass('application-menu-trigger');

    // Determine if the element requires the badge container
    if (isTertiaryButton || (isIconButton && !isMenuTrigger)) {
      const svgEl = this.element.find('svg');
      this.notificationBadgeContainerEl = $('<span class="notification-badge-container"></span>');
      this.notificationBadgeContainerEl.insertAfter(svgEl);
    } else {
      this.notificationBadgeContainerEl = $('<span class="notification-badge-container"></span>');
      this.element.append(this.notificationBadgeContainerEl);
    }

    // Add the notification badge to the container
    this.element.addClass('has-notification-badge');

    // Format the label value
    const formattedLabel = this.formatLabel();

    // Build HTML for notification badge
    const badgeClasses = `notification-dot notification-dot-${this.settings.position} notification-dot-${this.settings.color}`;
    const labelHtml = this.settings.label ? `<span class="notification-dot-label font-size-px-12">${formattedLabel}</span>` : '';
    const htmlNotificationBadge = `
      <span class="${badgeClasses} ${this.settings.label ? 'has-label' : ''}">
        <span aria-live="polite">${labelHtml}
          <span class="audible">${this.settings.audibleLabel || Locale.translate('Alert')}</span>
        </span>
      </span>
    `;

    // Append the notification badge to the container
    this.notificationBadgeContainerEl.append(htmlNotificationBadge);

    // Set label width
    this.setLabelWidth();

    // Add additional attributes for both container and dot
    const badgeContainer = this.element.find('.notification-badge-container');
    const notificationDot = badgeContainer.find('.notification-dot');

    utils.addAttributes(badgeContainer, this, this.settings.attributes, 'container');
    utils.addAttributes(notificationDot, this, this.settings.attributes, 'dot');

    if (this.settings.visible === false) {
      this.hide();
    }

    return this;
  },

  /**
   * Set the width of the label based on its content.
   * @returns {void}
   */
  setLabelWidth() {
    const labelEl = this.element.get(0).querySelector('.has-label');
    if (labelEl) {
      if (this.settings.label && this.settings.label.toString().length > 2) {
        labelEl.classList.add('label-wide');
      } else {
        labelEl.classList.remove('label-wide');
      }
    }
  },

  /**
   * Formats the label value based on its numeric size.
   * - If the value exceeds 999, it appends a suitable suffix (e.g., 'k', 'M') and a '+'.
   * - For non-numeric values, it returns the original label.
   * - If the value is an exact thousand, it returns the rounded value with the appropriate suffix.
   * @returns {string} The formatted label with a suffix or the original label if non-numeric or less than 1000.
   */
  formatLabel() {
    let labelValue = parseInt(this.settings.label, 10);

    if (isNaN(labelValue)) return this.settings.label; // Return the original label if it's not a number

    if (labelValue > 999) {
      const suffixes = ['', 'k', 'M', 'B', 'T'];
      let suffixIndex = 0;

      while (labelValue >= 1000 && suffixIndex < suffixes.length - 1) {
        labelValue /= 1000;
        suffixIndex++;
      }

      // Check if the labelValue is an exact thousand
      if (labelValue % 1 === 0) {
        return `${labelValue}${suffixes[suffixIndex]}`;
      }

      return `${Math.floor(labelValue)}${suffixes[suffixIndex]}+`;
    }

    return this.settings.label; // Return the original label if less than 1000
  },

  /**
   * Sets up event handlers for this component and its sub-elements.
   * @returns {object} The Component prototype, useful for chaining.
   * @private
   */
  handleEvents() {
    const self = this;

    this.element.on(`updated.${COMPONENT_NAME}`, () => {
      self.updated();
    });

    return this;
  },

  /**
   * Handle updated settings and values.
   * @param {object} settings Updated settings
   * @returns {object} The Component prototype, useful for chaining.
   */
  updated(settings) {
    if (settings) {
      this.settings = utils.mergeSettings(this.element, settings, this.settings);

      // Handle visible setting change efficiently
      if ('visible' in settings) {
        if (settings.visible) {
          this.show();
        } else {
          this.hide();
        }
        return this;
      }
    }
    return this
      .teardown()
      .init();
  },

  /**
    * Enable a disabled badge.
    * @returns {void}
    */
  show() {
    this.element.find('.notification-dot').removeClass('is-disabled');
  },

  /**
    * Disable a badge.
    * @returns {void}
    */
  hide() {
    this.element.find('.notification-dot').addClass('is-disabled');
  },

  /**
   * Simple Teardown - remove events & rebuildable markup.
   * @returns {object} The Component prototype, useful for chaining.
   * @private
   */
  teardown() {
    this.element.off(`updated.${COMPONENT_NAME}`);
    return this;
  },

  /**
   * Teardown - Remove added markup and events.
   * @private
   */
  destroy() {
    this.teardown();
    if (this.element) {
      this.element.find('.notification-badge-container').remove();
      $.removeData(this.element[0], COMPONENT_NAME);
    }
  }
};

export { NotificationBadge, COMPONENT_NAME };
